import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const AdminLoginLogsManagement = ({ onNavigate, userInfo = {} }) => {
    // Mobile state (used for responsive content layout)
    const [isMobile, setIsMobile] = useState(false);

    // State for login logs data
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination and search state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [limit] = useState(10);

    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        username: '',
        role: 'student',
        ip_address: '',
        user_agent: '',
        location: '',
        latitude: '',
        longitude: '',
        location_bypassed: 0,
        manual_location: 0,
        success: 1,
        status: 'active',
        logout_time: '',
        session_duration: ''
    });

    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    // Check if mobile on mount and resize
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1024);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Fetch login logs from API
    const fetchLogs = async (page = 1, search = '', role = '', date = '', status = '') => {
        try {
            setLoading(true);
            setError(null);

            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            if (!token) {
                setError('Authentication token not found. Please login again.');
                setLoading(false);
                return;
            }

            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...(search && search.trim() && { search: search.trim() }),
                ...(role && role.trim() && { role: role.trim() }),
                ...(date && date.trim() && { date: date.trim() }),
                ...(status && status.trim() && { status: status.trim() })
            });

            const apiUrl = `${API_LINK}/api/admin/login-logs?${params}`;

            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            if (!response.ok) {
                const errorText = await response.text();

                if (response.status === 401) {
                    setError('Session expired. Please login again.');
                    localStorage.removeItem('token');
                    sessionStorage.removeItem('token');
                    return;
                } else if (response.status === 404) {
                    setError('Login logs API endpoint not found. Please check if the backend server is running correctly.');
                    return;
                } else if (response.status === 500) {
                    setError('Server error occurred. Please check the backend logs.');
                    return;
                }

                throw new Error(`Server error: ${response.status} - ${errorText}`);
            }

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                setError('Server returned invalid response format. Expected JSON.');
                return;
            }

            const result = await response.json();

            if (result.success) {
                const logsData = result.data?.logs || [];
                const paginationData = result.data?.pagination || {};

                setLogs(logsData);
                setCurrentPage(paginationData.currentPage || page);
                setTotalPages(paginationData.totalPages || 1);
                setTotalCount(paginationData.totalCount || logsData.length);
            } else {
                setError(result.message || 'Failed to fetch login logs from server');
            }
        } catch (error) {
            if (error.name === 'TypeError' && error.message.includes('fetch')) {
                setError('Cannot connect to backend server. Please ensure:\n1. Backend server is running \n2. Admin API routes are properly configured\n3. Database connection is working');
            } else if (error.message.includes('JSON')) {
                setError('Server returned invalid data format. Please check backend API.');
            } else {
                setError(`Failed to load login logs: ${error.message}`);
            }
        } finally {
            setLoading(false);
        }
    };

    // Initial data fetch
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchLogs(1, '', '', '', '');
        }, 100);

        return () => clearTimeout(timer);
    }, []);

    // Handle search and filters
    const handleSearch = () => {
        setCurrentPage(1);
        fetchLogs(1, searchTerm, roleFilter, dateFilter, statusFilter);
    };

    // Handle pagination
    const handlePageChange = (page) => {
        if (page && page !== currentPage && page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            fetchLogs(page, searchTerm, roleFilter, dateFilter, statusFilter);
        }
    };

    // Validate form
    const validateForm = () => {
        const errors = {};

        if (!formData.username.trim()) errors.username = 'Username is required';
        if (!formData.role.trim()) errors.role = 'Role is required';
        if (!formData.ip_address.trim()) errors.ip_address = 'IP address is required';

        // IP address validation (basic)
        if (formData.ip_address && !/^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(formData.ip_address)) {
            errors.ip_address = 'Invalid IP address format';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle form submission for add/edit
    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setSubmitting(true);

            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const url = selectedLog
                ? `${API_LINK}/api/admin/login-logs/${selectedLog.id}`
                : `${API_LINK}/api/admin/login-logs`;

            const method = selectedLog ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            const result = await response.json();

            if (result.success) {
                setShowAddModal(false);
                setShowEditModal(false);
                resetForm();
                fetchLogs(currentPage || 1, searchTerm, roleFilter, dateFilter, statusFilter);
                alert(selectedLog ? 'Login log updated successfully!' : 'Login log added successfully!');
            } else {
                alert(result.message || 'Operation failed');
            }
        } catch (error) {
            alert('Failed to save login log. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    // Handle delete
    const handleDelete = async () => {
        if (!selectedLog) return;

        try {
            setSubmitting(true);

            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const response = await fetch(`${API_LINK}/api/admin/login-logs/${selectedLog.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const result = await response.json();

            if (result.success) {
                setShowDeleteModal(false);
                setSelectedLog(null);
                fetchLogs(currentPage || 1, searchTerm, roleFilter, dateFilter, statusFilter);
                alert('Login log deleted successfully!');
            } else {
                alert(result.message || 'Delete failed');
            }
        } catch (error) {
            alert('Failed to delete login log. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            username: '',
            role: 'student',
            ip_address: '',
            user_agent: '',
            location: '',
            latitude: '',
            longitude: '',
            location_bypassed: 0,
            manual_location: 0,
            success: 1,
            status: 'active',
            logout_time: '',
            session_duration: ''
        });
        setFormErrors({});
        setSelectedLog(null);
    };

    // Handle edit click
    const handleEditClick = (log) => {
        setSelectedLog(log);
        setFormData({
            username: log.username || '',
            role: log.role || 'student',
            ip_address: log.ip_address || '',
            user_agent: log.user_agent || '',
            location: log.location || '',
            latitude: log.latitude?.toString() || '',
            longitude: log.longitude?.toString() || '',
            location_bypassed: log.location_bypassed || 0,
            manual_location: log.manual_location || 0,
            success: log.success || 1,
            status: log.status || 'active',
            logout_time: log.logout_time ? log.logout_time.slice(0, 16) : '',
            session_duration: log.session_duration?.toString() || ''
        });
        setShowEditModal(true);
    };

    // Handle view click
    const handleViewClick = (log) => {
        setSelectedLog(log);
        setShowViewModal(true);
    };

    // Handle delete click
    const handleDeleteClick = (log) => {
        setSelectedLog(log);
        setShowDeleteModal(true);
    };



    // Get role badge color
    const getRoleBadgeColor = (role) => {
        switch (role) {
            case 'admin':
                return { backgroundColor: '#fee2e2', color: '#dc2626' };
            case 'teacher':
                return { backgroundColor: '#e0f2fe', color: '#0277bd' };
            case 'student':
                return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
            case 'hod':
                return { backgroundColor: '#fff3e0', color: '#f57c00' };
            default:
                return { backgroundColor: '#f5f5f5', color: '#666' };
        }
    };

    // Get status badge color
    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'active':
                return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
            case 'suspicious':
                return { backgroundColor: '#fff3e0', color: '#f57c00' };
            case 'blocked':
                return { backgroundColor: '#fee2e2', color: '#dc2626' };
            default:
                return { backgroundColor: '#f5f5f5', color: '#666' };
        }
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    // Format session duration
    const formatSessionDuration = (seconds) => {
        if (!seconds) return 'N/A';
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hours}h ${minutes}m ${secs}s`;
    };

    return (
        <Layout role="admin" onNavigate={onNavigate} activePage="admin-login-logs" userInfo={userInfo}>
            {/* Welcome Banner */}
            <div style={{
                background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                color: '#ffffff',
                padding: '2rem',
                borderRadius: '12px',
                marginBottom: '2rem',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
                <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>
                    Login Logs Management 📋
                </h2>
                <p style={{ margin: 0, opacity: 0.9 }}>
                    Monitor and manage user login activities, track security events, and maintain system access logs.
                </p>
            </div>

            {/* Search and Controls Section */}
            <div style={{
                background: '#ffffff',
                padding: '2rem',
                borderRadius: '12px',
                marginBottom: '2rem',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
                <div style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'stretch' : 'center',
                    gap: '1rem',
                    marginBottom: '1rem'
                }}>
                    {/* Search Bar */}
                    <div style={{ flex: 1, maxWidth: isMobile ? '100%' : '300px' }}>
                        <div style={{ position: 'relative' }}>
                            <input
                                type="text"
                                placeholder="Search by username, IP address..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                style={{
                                    width: '100%',
                                    padding: '1rem 3rem 1rem 1rem',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '1rem',
                                    outline: 'none',
                                    transition: 'all 0.3s ease'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#238548';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(35, 133, 72, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#e2e8f0';
                                    e.target.style.boxShadow = 'none';
                                }}
                            />
                            <button
                                onClick={handleSearch}
                                style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '1.2rem',
                                    cursor: 'pointer',
                                    color: '#666',
                                    padding: '0.5rem'
                                }}
                            >
                                🔍
                            </button>
                        </div>
                    </div>

                    {/* Role Filter */}
                    <select
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                        style={{
                            padding: '1rem',
                            border: '2px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            outline: 'none',
                            minWidth: '120px'
                        }}
                    >
                        <option value="">All Roles</option>
                        <option value="admin">Admin</option>
                        <option value="teacher">Teacher</option>
                        <option value="student">Student</option>
                        <option value="hod">HOD</option>
                    </select>

                    {/* Date Filter */}
                    <input
                        type="date"
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        style={{
                            padding: '1rem',
                            border: '2px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            outline: 'none',
                            minWidth: '150px'
                        }}
                    />

                    {/* Status Filter */}
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        style={{
                            padding: '1rem',
                            border: '2px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            outline: 'none',
                            minWidth: '120px'
                        }}
                    >
                        <option value="">All Status</option>
                        <option value="active">Active</option>
                        <option value="suspicious">Suspicious</option>
                        <option value="blocked">Blocked</option>
                    </select>

                    {/* Add Log Button */}
                    <button
                        onClick={() => {
                            resetForm();
                            setShowAddModal(true);
                        }}
                        style={{
                            padding: '1rem 2rem',
                            background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 8px 25px rgba(35, 133, 72, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                        }}
                    >
                        <span style={{ fontSize: '1.2rem' }}>+</span>
                        Add Log Entry
                    </button>
                </div>

                {/* Stats */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: '1rem',
                    borderTop: '1px solid #ecf0f1',
                    fontSize: '0.9rem',
                    color: '#666'
                }}>
                    <span>
                        {loading ? 'Loading login logs...' :
                            error ? 'Error loading login logs' :
                                `Showing ${logs.length} of ${totalCount} login logs`}
                        {(searchTerm || roleFilter || dateFilter || statusFilter) && !loading && !error && ' (filtered)'}
                    </span>
                    {loading && (
                        <span style={{ color: '#238548' }}>
                            <div style={{
                                width: '16px',
                                height: '16px',
                                border: '2px solid #e3f2fd',
                                borderTop: '2px solid #238548',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                                display: 'inline-block'
                            }}></div>
                        </span>
                    )}
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div style={{
                    background: '#fee2e2',
                    border: '1px solid #fecaca',
                    color: '#dc2626',
                    padding: '1rem',
                    borderRadius: '12px',
                    marginBottom: '2rem'
                }}>
                    <p style={{ margin: '0 0 0.5rem 0', fontWeight: '500' }}>⚠️ Error Loading Login Logs</p>
                    <p style={{ margin: '0 0 1rem 0' }}>{error}</p>
                    <button
                        onClick={() => fetchLogs(currentPage, searchTerm, roleFilter, dateFilter, statusFilter)}
                        style={{
                            background: '#dc2626',
                            color: 'white',
                            border: 'none',
                            padding: '0.5rem 1rem',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.9rem'
                        }}
                    >
                        Try Again
                    </button>
                </div>
            )}

            {/* Login Logs Table */}
            <div style={{
                background: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                overflow: 'hidden'
            }}>
                {loading && logs.length === 0 ? (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '4rem',
                        flexDirection: 'column',
                        gap: '1rem'
                    }}>
                        <div style={{
                            width: '50px',
                            height: '50px',
                            border: '4px solid #e3f2fd',
                            borderTop: '4px solid #238548',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                        }}></div>
                        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading login logs...</p>
                        <style>
                            {`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}
                        </style>
                    </div>
                ) : logs.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '4rem 2rem'
                    }}>
                        <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: 0.5 }}>📋</div>
                        <h3 style={{
                            fontSize: '1.5rem',
                            fontWeight: 600,
                            color: '#333',
                            margin: '0 0 1rem 0'
                        }}>
                            {searchTerm || roleFilter || dateFilter || statusFilter ? 'No login logs found' : 'No login logs available'}
                        </h3>
                        <p style={{ color: '#666', margin: '0 0 2rem 0', fontSize: '1rem' }}>
                            {searchTerm || roleFilter || dateFilter || statusFilter
                                ? 'Try adjusting your search criteria or filters'
                                : 'Login logs will appear here as users access the system'
                            }
                        </p>
                    </div>
                ) : (
                    <>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                fontSize: isMobile ? '0.85rem' : '0.95rem'
                            }}>
                                <thead>
                                    <tr style={{
                                        background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                                        color: '#ffffff'
                                    }}>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            fontWeight: 600,
                                            borderBottom: 'none',
                                            whiteSpace: 'nowrap'
                                        }}>ID</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            fontWeight: 600,
                                            borderBottom: 'none',
                                            whiteSpace: 'nowrap'
                                        }}>Username</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            fontWeight: 600,
                                            borderBottom: 'none',
                                            whiteSpace: 'nowrap'
                                        }}>Role</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            fontWeight: 600,
                                            borderBottom: 'none',
                                            whiteSpace: 'nowrap'
                                        }}>Login Time</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            fontWeight: 600,
                                            borderBottom: 'none',
                                            whiteSpace: 'nowrap'
                                        }}>IP Address</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            fontWeight: 600,
                                            borderBottom: 'none',
                                            whiteSpace: 'nowrap'
                                        }}>Status</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'center',
                                            fontWeight: 600,
                                            borderBottom: 'none',
                                            whiteSpace: 'nowrap'
                                        }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.map((log, index) => (
                                        <tr key={log.id} style={{
                                            borderBottom: '1px solid #f1f5f9',
                                            transition: 'background-color 0.3s ease'
                                        }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            <td style={{ padding: '1rem', fontWeight: 500, color: '#238548' }}>
                                                {log.id}
                                            </td>
                                            <td style={{ padding: '1rem' }}>
                                                <div style={{ fontWeight: 500, color: '#333' }}>
                                                    {log.username}
                                                </div>
                                            </td>
                                            <td style={{ padding: '1rem' }}>
                                                <span style={{
                                                    padding: '0.25rem 0.75rem',
                                                    borderRadius: '20px',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 500,
                                                    textTransform: 'capitalize',
                                                    ...getRoleBadgeColor(log.role)
                                                }}>
                                                    {log.role}
                                                </span>
                                            </td>
                                            <td style={{ padding: '1rem', color: '#666' }}>
                                                {formatDate(log.login_time)}
                                            </td>
                                            <td style={{ padding: '1rem', color: '#666', fontFamily: 'monospace' }}>
                                                {log.ip_address}
                                            </td>
                                            <td style={{ padding: '1rem' }}>
                                                <span style={{
                                                    padding: '0.25rem 0.75rem',
                                                    borderRadius: '20px',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 500,
                                                    textTransform: 'capitalize',
                                                    ...getStatusBadgeColor(log.status)
                                                }}>
                                                    {log.status}
                                                </span>
                                            </td>
                                            <td style={{ padding: '1rem', textAlign: 'center' }}>
                                                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                                                    <button
                                                        onClick={() => handleViewClick(log)}
                                                        style={{
                                                            padding: '0.5rem 0.75rem',
                                                            backgroundColor: '#f0fdf4',
                                                            color: '#16a34a',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            fontSize: '0.85rem',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.3s ease',
                                                            fontWeight: 500
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.target.style.backgroundColor = '#16a34a';
                                                            e.target.style.color = '#ffffff';
                                                            e.target.style.transform = 'translateY(-1px)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.target.style.backgroundColor = '#f0fdf4';
                                                            e.target.style.color = '#16a34a';
                                                            e.target.style.transform = 'translateY(0)';
                                                        }}
                                                    >
                                                        👁️ View
                                                    </button>
                                                    <button
                                                        onClick={() => handleEditClick(log)}
                                                        style={{
                                                            padding: '0.5rem 0.75rem',
                                                            backgroundColor: '#e3f2fd',
                                                            color: '#1976d2',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            fontSize: '0.85rem',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.3s ease',
                                                            fontWeight: 500
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.target.style.backgroundColor = '#1976d2';
                                                            e.target.style.color = '#ffffff';
                                                            e.target.style.transform = 'translateY(-1px)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.target.style.backgroundColor = '#e3f2fd';
                                                            e.target.style.color = '#1976d2';
                                                            e.target.style.transform = 'translateY(0)';
                                                        }}
                                                    >
                                                        ✏️ Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteClick(log)}
                                                        style={{
                                                            padding: '0.5rem 0.75rem',
                                                            backgroundColor: '#ffebee',
                                                            color: '#d32f2f',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            fontSize: '0.85rem',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.3s ease',
                                                            fontWeight: 500
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.target.style.backgroundColor = '#d32f2f';
                                                            e.target.style.color = '#ffffff';
                                                            e.target.style.transform = 'translateY(-1px)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.target.style.backgroundColor = '#ffebee';
                                                            e.target.style.color = '#d32f2f';
                                                            e.target.style.transform = 'translateY(0)';
                                                        }}
                                                    >
                                                        🗑️ Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div style={{
                                padding: '1.5rem',
                                borderTop: '1px solid #f1f5f9',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '1rem'
                            }}>
                                <div style={{ color: '#666', fontSize: '0.9rem' }}>
                                    Page {currentPage} of {totalPages} • {totalCount} total login logs
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        style={{
                                            padding: '0.5rem 1rem',
                                            border: '2px solid #e2e8f0',
                                            backgroundColor: currentPage === 1 ? '#f8fafc' : '#ffffff',
                                            color: currentPage === 1 ? '#9ca3af' : '#374151',
                                            borderRadius: '6px',
                                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                            transition: 'all 0.3s ease',
                                            fontSize: '0.9rem'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (currentPage !== 1) {
                                                e.target.style.borderColor = '#238548';
                                                e.target.style.backgroundColor = '#f0fdf4';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (currentPage !== 1) {
                                                e.target.style.borderColor = '#e2e8f0';
                                                e.target.style.backgroundColor = '#ffffff';
                                            }
                                        }}
                                    >
                                        ← Previous
                                    </button>

                                    {[...Array(Math.min(5, totalPages))].map((_, index) => {
                                        let page;
                                        if (totalPages <= 5) {
                                            page = index + 1;
                                        } else if (currentPage <= 3) {
                                            page = index + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            page = totalPages - 4 + index;
                                        } else {
                                            page = currentPage - 2 + index;
                                        }

                                        return (
                                            <button
                                                key={page}
                                                onClick={() => handlePageChange(page)}
                                                style={{
                                                    padding: '0.5rem 0.75rem',
                                                    border: '2px solid',
                                                    borderColor: page === currentPage ? '#238548' : '#e2e8f0',
                                                    backgroundColor: page === currentPage ? '#238548' : '#ffffff',
                                                    color: page === currentPage ? '#ffffff' : '#374151',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    fontSize: '0.9rem',
                                                    fontWeight: page === currentPage ? '600' : '400'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (page !== currentPage) {
                                                        e.target.style.borderColor = '#238548';
                                                        e.target.style.backgroundColor = '#f0fdf4';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (page !== currentPage) {
                                                        e.target.style.borderColor = '#e2e8f0';
                                                        e.target.style.backgroundColor = '#ffffff';
                                                    }
                                                }}
                                            >
                                                {page}
                                            </button>
                                        );
                                    })}

                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        style={{
                                            padding: '0.5rem 1rem',
                                            border: '2px solid #e2e8f0',
                                            backgroundColor: currentPage === totalPages ? '#f8fafc' : '#ffffff',
                                            color: currentPage === totalPages ? '#9ca3af' : '#374151',
                                            borderRadius: '6px',
                                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                            transition: 'all 0.3s ease',
                                            fontSize: '0.9rem'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (currentPage !== totalPages) {
                                                e.target.style.borderColor = '#238548';
                                                e.target.style.backgroundColor = '#f0fdf4';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (currentPage !== totalPages) {
                                                e.target.style.borderColor = '#e2e8f0';
                                                e.target.style.backgroundColor = '#ffffff';
                                            }
                                        }}
                                    >
                                        Next →
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Add/Edit Modal */}
            {(showAddModal || showEditModal) && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2000,
                    padding: '1rem'
                }}>
                    <div style={{
                        background: '#ffffff',
                        borderRadius: '12px',
                        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
                        width: '100%',
                        maxWidth: '900px',
                        maxHeight: '90vh',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        {/* Modal Header */}
                        <div style={{
                            background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                            color: '#ffffff',
                            padding: '1.5rem 2rem',
                            borderRadius: '12px 12px 0 0'
                        }}>
                            <h3 style={{
                                margin: 0,
                                fontSize: '1.5rem',
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                {selectedLog ? '✏️ Edit Login Log' : '➕ Add New Login Log'}
                            </h3>
                            <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9, fontSize: '0.95rem' }}>
                                {selectedLog ? 'Update login log information' : 'Enter new login log details'}
                            </p>
                        </div>

                        {/* Modal Content */}
                        <div style={{
                            padding: '2rem',
                            overflowY: 'auto',
                            flex: 1
                        }}>
                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1.5rem' }}>
                                {/* Username */}
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '0.9rem',
                                        fontWeight: 600,
                                        color: '#374151',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Username *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.username}
                                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                        placeholder="Enter username"
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: `2px solid ${formErrors.username ? '#dc2626' : '#e5e7eb'}`,
                                            borderRadius: '8px',
                                            fontSize: '1rem',
                                            outline: 'none',
                                            transition: 'border-color 0.3s ease',
                                            backgroundColor: formErrors.username ? '#fef2f2' : '#ffffff'
                                        }}
                                        onFocus={(e) => {
                                            if (!formErrors.username) e.target.style.borderColor = '#238548';
                                        }}
                                        onBlur={(e) => {
                                            if (!formErrors.username) e.target.style.borderColor = '#e5e7eb';
                                        }}
                                    />
                                    {formErrors.username && (
                                        <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                                            {formErrors.username}
                                        </p>
                                    )}
                                </div>

                                {/* Role */}
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '0.9rem',
                                        fontWeight: 600,
                                        color: '#374151',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Role *
                                    </label>
                                    <select
                                        value={formData.role}
                                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: `2px solid ${formErrors.role ? '#dc2626' : '#e5e7eb'}`,
                                            borderRadius: '8px',
                                            fontSize: '1rem',
                                            outline: 'none',
                                            transition: 'border-color 0.3s ease',
                                            backgroundColor: formErrors.role ? '#fef2f2' : '#ffffff'
                                        }}
                                        onFocus={(e) => {
                                            if (!formErrors.role) e.target.style.borderColor = '#238548';
                                        }}
                                        onBlur={(e) => {
                                            if (!formErrors.role) e.target.style.borderColor = '#e5e7eb';
                                        }}
                                    >
                                        <option value="student">Student</option>
                                        <option value="teacher">Teacher</option>
                                        <option value="hod">HOD</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                    {formErrors.role && (
                                        <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                                            {formErrors.role}
                                        </p>
                                    )}
                                </div>

                                {/* IP Address */}
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '0.9rem',
                                        fontWeight: 600,
                                        color: '#374151',
                                        marginBottom: '0.5rem'
                                    }}>
                                        IP Address *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.ip_address}
                                        onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                                        placeholder="e.g., 192.168.1.1"
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: `2px solid ${formErrors.ip_address ? '#dc2626' : '#e5e7eb'}`,
                                            borderRadius: '8px',
                                            fontSize: '1rem',
                                            outline: 'none',
                                            transition: 'border-color 0.3s ease',
                                            backgroundColor: formErrors.ip_address ? '#fef2f2' : '#ffffff'
                                        }}
                                        onFocus={(e) => {
                                            if (!formErrors.ip_address) e.target.style.borderColor = '#238548';
                                        }}
                                        onBlur={(e) => {
                                            if (!formErrors.ip_address) e.target.style.borderColor = '#e5e7eb';
                                        }}
                                    />
                                    {formErrors.ip_address && (
                                        <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                                            {formErrors.ip_address}
                                        </p>
                                    )}
                                </div>

                                {/* User Agent */}
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '0.9rem',
                                        fontWeight: 600,
                                        color: '#374151',
                                        marginBottom: '0.5rem'
                                    }}>
                                        User Agent
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.user_agent}
                                        onChange={(e) => setFormData({ ...formData, user_agent: e.target.value })}
                                        placeholder="Browser/device information"
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '2px solid #e5e7eb',
                                            borderRadius: '8px',
                                            fontSize: '1rem',
                                            outline: 'none',
                                            transition: 'border-color 0.3s ease'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#238548'}
                                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                    />
                                </div>

                                {/* Location */}
                                <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '0.9rem',
                                        fontWeight: 600,
                                        color: '#374151',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Location
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.location}
                                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                        placeholder="Geographic location or description"
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '2px solid #e5e7eb',
                                            borderRadius: '8px',
                                            fontSize: '1rem',
                                            outline: 'none',
                                            transition: 'border-color 0.3s ease'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#238548'}
                                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                    />
                                </div>

                                {/* Latitude */}
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '0.9rem',
                                        fontWeight: 600,
                                        color: '#374151',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Latitude
                                    </label>
                                    <input
                                        type="number"
                                        step="any"
                                        value={formData.latitude}
                                        onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                                        placeholder="e.g., 22.5726"
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '2px solid #e5e7eb',
                                            borderRadius: '8px',
                                            fontSize: '1rem',
                                            outline: 'none',
                                            transition: 'border-color 0.3s ease'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#238548'}
                                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                    />
                                </div>

                                {/* Longitude */}
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '0.9rem',
                                        fontWeight: 600,
                                        color: '#374151',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Longitude
                                    </label>
                                    <input
                                        type="number"
                                        step="any"
                                        value={formData.longitude}
                                        onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                                        placeholder="e.g., 88.3639"
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '2px solid #e5e7eb',
                                            borderRadius: '8px',
                                            fontSize: '1rem',
                                            outline: 'none',
                                            transition: 'border-color 0.3s ease'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#238548'}
                                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                    />
                                </div>

                                {/* Status */}
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '0.9rem',
                                        fontWeight: 600,
                                        color: '#374151',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Status
                                    </label>
                                    <select
                                        value={formData.status}
                                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '2px solid #e5e7eb',
                                            borderRadius: '8px',
                                            fontSize: '1rem',
                                            outline: 'none',
                                            transition: 'border-color 0.3s ease'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#238548'}
                                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                    >
                                        <option value="active">Active</option>
                                        <option value="suspicious">Suspicious</option>
                                        <option value="blocked">Blocked</option>
                                    </select>
                                </div>

                                {/* Success */}
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '0.9rem',
                                        fontWeight: 600,
                                        color: '#374151',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Login Success
                                    </label>
                                    <select
                                        value={formData.success}
                                        onChange={(e) => setFormData({ ...formData, success: parseInt(e.target.value) })}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '2px solid #e5e7eb',
                                            borderRadius: '8px',
                                            fontSize: '1rem',
                                            outline: 'none',
                                            transition: 'border-color 0.3s ease'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#238548'}
                                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                    >
                                        <option value={1}>Successful</option>
                                        <option value={0}>Failed</option>
                                    </select>
                                </div>

                                {/* Logout Time */}
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '0.9rem',
                                        fontWeight: 600,
                                        color: '#374151',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Logout Time
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={formData.logout_time}
                                        onChange={(e) => setFormData({ ...formData, logout_time: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '2px solid #e5e7eb',
                                            borderRadius: '8px',
                                            fontSize: '1rem',
                                            outline: 'none',
                                            transition: 'border-color 0.3s ease'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#238548'}
                                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                    />
                                </div>

                                {/* Session Duration */}
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '0.9rem',
                                        fontWeight: 600,
                                        color: '#374151',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Session Duration (seconds)
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.session_duration}
                                        onChange={(e) => setFormData({ ...formData, session_duration: e.target.value })}
                                        placeholder="Duration in seconds"
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '2px solid #e5e7eb',
                                            borderRadius: '8px',
                                            fontSize: '1rem',
                                            outline: 'none',
                                            transition: 'border-color 0.3s ease'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#238548'}
                                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div style={{
                            padding: '1.5rem 2rem',
                            borderTop: '1px solid #f1f5f9',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '1rem',
                            backgroundColor: '#f8fafc'
                        }}>
                            <button
                                onClick={() => {
                                    setShowAddModal(false);
                                    setShowEditModal(false);
                                    resetForm();
                                }}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    border: '2px solid #e5e7eb',
                                    backgroundColor: '#ffffff',
                                    color: '#374151',
                                    borderRadius: '8px',
                                    fontSize: '1rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    fontWeight: 500
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.borderColor = '#d1d5db';
                                    e.target.style.backgroundColor = '#f9fafb';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.borderColor = '#e5e7eb';
                                    e.target.style.backgroundColor = '#ffffff';
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={submitting}
                                style={{
                                    padding: '0.75rem 2rem',
                                    border: 'none',
                                    background: submitting
                                        ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                                        : 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                                    color: '#ffffff',
                                    borderRadius: '8px',
                                    fontSize: '1rem',
                                    cursor: submitting ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.3s ease',
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}
                                onMouseEnter={(e) => {
                                    if (!submitting) {
                                        e.target.style.transform = 'translateY(-2px)';
                                        e.target.style.boxShadow = '0 8px 25px rgba(35, 133, 72, 0.3)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!submitting) {
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = 'none';
                                    }
                                }}
                            >
                                {submitting ? (
                                    <>
                                        <div style={{
                                            width: '16px',
                                            height: '16px',
                                            border: '2px solid #ffffff',
                                            borderTop: '2px solid transparent',
                                            borderRadius: '50%',
                                            animation: 'spin 1s linear infinite'
                                        }}></div>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        {selectedLog ? '💾 Update Log' : '➕ Add Log'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* View Modal */}
            {showViewModal && selectedLog && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2000,
                    padding: '1rem'
                }}>
                    <div style={{
                        background: '#ffffff',
                        borderRadius: '12px',
                        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
                        maxWidth: '600px',
                        width: '100%'
                    }}>
                        {/* Modal Header */}
                        <div style={{
                            background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                            color: '#ffffff',
                            padding: '1.5rem 2rem',
                            borderRadius: '12px 12px 0 0'
                        }}>
                            <h3 style={{
                                margin: 0,
                                fontSize: '1.5rem',
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                👁️ View Login Log
                            </h3>
                            <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9, fontSize: '0.95rem' }}>
                                Login log details and session information
                            </p>
                        </div>

                        {/* Modal Content */}
                        <div style={{ padding: '2rem' }}>
                            <div style={{ display: 'grid', gap: '1.5rem' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    <div>
                                        <label style={{ fontSize: '0.9rem', fontWeight: 600, color: '#666', display: 'block', marginBottom: '0.5rem' }}>
                                            Log ID
                                        </label>
                                        <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: 500, color: '#333' }}>
                                            {selectedLog.id}
                                        </p>
                                    </div>

                                    <div>
                                        <label style={{ fontSize: '0.9rem', fontWeight: 600, color: '#666', display: 'block', marginBottom: '0.5rem' }}>
                                            Username
                                        </label>
                                        <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: 500, color: '#333' }}>
                                            {selectedLog.username}
                                        </p>
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    <div>
                                        <label style={{ fontSize: '0.9rem', fontWeight: 600, color: '#666', display: 'block', marginBottom: '0.5rem' }}>
                                            Role
                                        </label>
                                        <span style={{
                                            padding: '0.5rem 1rem',
                                            borderRadius: '20px',
                                            fontSize: '0.9rem',
                                            fontWeight: 500,
                                            textTransform: 'capitalize',
                                            ...getRoleBadgeColor(selectedLog.role)
                                        }}>
                                            {selectedLog.role}
                                        </span>
                                    </div>

                                    <div>
                                        <label style={{ fontSize: '0.9rem', fontWeight: 600, color: '#666', display: 'block', marginBottom: '0.5rem' }}>
                                            Status
                                        </label>
                                        <span style={{
                                            padding: '0.5rem 1rem',
                                            borderRadius: '20px',
                                            fontSize: '0.9rem',
                                            fontWeight: 500,
                                            textTransform: 'capitalize',
                                            ...getStatusBadgeColor(selectedLog.status)
                                        }}>
                                            {selectedLog.status}
                                        </span>
                                    </div>
                                </div>

                                <div>
                                    <label style={{ fontSize: '0.9rem', fontWeight: 600, color: '#666', display: 'block', marginBottom: '0.5rem' }}>
                                        Login Time
                                    </label>
                                    <p style={{ margin: 0, fontSize: '1rem', color: '#333' }}>
                                        {formatDate(selectedLog.login_time)}
                                    </p>
                                </div>

                                <div>
                                    <label style={{ fontSize: '0.9rem', fontWeight: 600, color: '#666', display: 'block', marginBottom: '0.5rem' }}>
                                        IP Address
                                    </label>
                                    <p style={{ margin: 0, fontSize: '1rem', color: '#333', fontFamily: 'monospace' }}>
                                        {selectedLog.ip_address}
                                    </p>
                                </div>

                                <div>
                                    <label style={{ fontSize: '0.9rem', fontWeight: 600, color: '#666', display: 'block', marginBottom: '0.5rem' }}>
                                        Location
                                    </label>
                                    <p style={{ margin: 0, fontSize: '1rem', color: '#333' }}>
                                        {selectedLog.location || 'Not provided'}
                                    </p>
                                </div>

                                <div>
                                    <label style={{ fontSize: '0.9rem', fontWeight: 600, color: '#666', display: 'block', marginBottom: '0.5rem' }}>
                                        Session Duration
                                    </label>
                                    <p style={{ margin: 0, fontSize: '1rem', color: '#333' }}>
                                        {formatSessionDuration(selectedLog.session_duration)}
                                    </p>
                                </div>

                                {selectedLog.logout_time && (
                                    <div>
                                        <label style={{ fontSize: '0.9rem', fontWeight: 600, color: '#666', display: 'block', marginBottom: '0.5rem' }}>
                                            Logout Time
                                        </label>
                                        <p style={{ margin: 0, fontSize: '1rem', color: '#333' }}>
                                            {formatDate(selectedLog.logout_time)}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div style={{
                            padding: '1.5rem 2rem',
                            borderTop: '1px solid #f1f5f9',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            backgroundColor: '#f8fafc'
                        }}>
                            <button
                                onClick={() => {
                                    setShowViewModal(false);
                                    setSelectedLog(null);
                                }}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    border: '2px solid #e5e7eb',
                                    backgroundColor: '#ffffff',
                                    color: '#374151',
                                    borderRadius: '8px',
                                    fontSize: '1rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    fontWeight: 500
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.borderColor = '#d1d5db';
                                    e.target.style.backgroundColor = '#f9fafb';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.borderColor = '#e5e7eb';
                                    e.target.style.backgroundColor = '#ffffff';
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && selectedLog && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2000,
                    padding: '1rem'
                }}>
                    <div style={{
                        background: '#ffffff',
                        borderRadius: '12px',
                        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
                        maxWidth: '400px',
                        width: '100%'
                    }}>
                        {/* Modal Header */}
                        <div style={{
                            padding: '2rem 2rem 1rem 2rem',
                            textAlign: 'center'
                        }}>
                            <div style={{
                                width: '64px',
                                height: '64px',
                                background: '#fee2e2',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 1rem',
                                fontSize: '2rem'
                            }}>
                                🗑️
                            </div>
                            <h3 style={{
                                margin: '0 0 1rem 0',
                                fontSize: '1.5rem',
                                fontWeight: 600,
                                color: '#dc2626'
                            }}>
                                Delete Login Log
                            </h3>
                            <p style={{
                                margin: '0 0 1rem 0',
                                color: '#6b7280',
                                lineHeight: 1.6
                            }}>
                                Are you sure you want to delete the login log for <strong style={{ color: '#374151' }}>{selectedLog.username}</strong> on {formatDate(selectedLog.login_time)}?
                            </p>
                            <p style={{
                                margin: 0,
                                color: '#dc2626',
                                fontSize: '0.9rem',
                                fontWeight: 500
                            }}>
                                This action cannot be undone and will permanently remove this log entry.
                            </p>
                        </div>

                        {/* Modal Footer */}
                        <div style={{
                            padding: '1rem 2rem 2rem 2rem',
                            display: 'flex',
                            justifyContent: 'center',
                            gap: '1rem'
                        }}>
                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setSelectedLog(null);
                                }}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    border: '2px solid #e5e7eb',
                                    backgroundColor: '#ffffff',
                                    color: '#374151',
                                    borderRadius: '8px',
                                    fontSize: '1rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    fontWeight: 500
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.borderColor = '#d1d5db';
                                    e.target.style.backgroundColor = '#f9fafb';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.borderColor = '#e5e7eb';
                                    e.target.style.backgroundColor = '#ffffff';
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={submitting}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    border: 'none',
                                    background: submitting
                                        ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                                        : 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)',
                                    color: '#ffffff',
                                    borderRadius: '8px',
                                    fontSize: '1rem',
                                    cursor: submitting ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.3s ease',
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}
                                onMouseEnter={(e) => {
                                    if (!submitting) {
                                        e.target.style.transform = 'translateY(-2px)';
                                        e.target.style.boxShadow = '0 8px 25px rgba(220, 38, 38, 0.3)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!submitting) {
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = 'none';
                                    }
                                }}
                            >
                                {submitting ? (
                                    <>
                                        <div style={{
                                            width: '16px',
                                            height: '16px',
                                            border: '2px solid #ffffff',
                                            borderTop: '2px solid transparent',
                                            borderRadius: '50%',
                                            animation: 'spin 1s linear infinite'
                                        }}></div>
                                        Deleting...
                                    </>
                                ) : (
                                    '🗑️ Delete Log'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default AdminLoginLogsManagement;